/**
 * MenuToggleButton — Binary mode-toggle button inside the MenuBar pill.
 *
 * Unlike MenuButton (which opens a SubMenuPanel), this button toggles a
 * boolean mode directly — with a visual icon swap to reflect the state.
 *
 * Examples:
 *   • Studio ↔ Road environment   (world icon ↔ studio icon)
 *   • Day ↔ Night                 (lightOn icon ↔ lightOff icon)
 *   • Doors closed ↔ open         (openDoor icon ↔ closeDoor icon)
 *
 * Button type taxonomy (MenuBar):
 *   MenuButton       — opens/closes a SubMenuPanel slot
 *   MenuToggleButton — binary mode toggle with icon swap   ← this component
 *   MenuButton(info) — submenu toggle for overlay screens
 *
 * @see docs/ims-brand-frontends/08-ui-screens.md#bottom-menu-bar
 */
import { cn } from '../../utils/cn';
import { useUiTheme } from '../../hooks/useUiTheme';

interface MenuToggleButtonProps {
  /** Icon shown when toggle is OFF (default state). */
  icon: string;
  /** Icon shown when toggle is ON (active state). */
  iconActive: string;
  /** Accessible label. */
  label: string;
  /** Current toggle state — controlled externally. */
  isActive: boolean;
  onClick: () => void;
  className?: string;
}

export function MenuToggleButton({
  icon,
  iconActive,
  label,
  isActive,
  onClick,
  className,
}: MenuToggleButtonProps) {
  const theme = useUiTheme();

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      aria-label={label}
      className={cn(
        'w-11 h-11 rounded-full flex items-center justify-center',
        'transition-all duration-200 cursor-pointer',
        isActive ? theme.btnActive : theme.btnInactive,
        className,
      )}
    >
      <img
        src={isActive ? iconActive : icon}
        alt=""
        aria-hidden
        className="w-6.5 h-6.5"
      />
    </button>
  );
}
